import type { ReactNode } from "react";
import { useEffect, useMemo, useRef } from "react";
import type { ConnectedProps } from "react-redux";
import { connect } from "react-redux";
import type { DecoratedFormProps } from "redux-form";
import {
  clearFields,
  Field,
  formValueSelector,
  reduxForm,
  stopSubmit,
} from "redux-form";
import _ from "underscore";

import {
  Box,
  Divider,
  Loader,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { Form } from "@js/forms/components/form";
import { DateField, SelectField } from "@js/forms/fields";
import { TextField, ToggleButtonGroupField } from "@js/forms/fields";
import { useLatestCallback } from "@js/hooks/use-latest-callback";
import type { AppDispatch, RootState } from "@js/store";
import type {
  WithdrawalFieldRequirement,
  WithdrawalFormRequirements,
} from "@js/types/withdrawals";
import { mapTypedDispatchToProps } from "@js/utils/store";

import {
  fetchCurrencyRequirements,
  fetchOtherRequirements,
  fetchTransferWiseCurrencies,
} from "../actions";
import { AddWithdrawalMethodOTPModalInstance } from "../components/add-withdrawal-method/withdrawal-method-otp-modal-instance";

export const ADD_TRANSFERWISE_WITHDRAWAL_FORM_ID = "addTransferWiseWithdrawal";
const selector = formValueSelector(ADD_TRANSFERWISE_WITHDRAWAL_FORM_ID);

type AddTransferWiseWithdrawalFormProps = ConnectedProps<typeof connector> & {
  formRequirements: WithdrawalFormRequirements;
} & DecoratedFormProps;

const requiredValidator = (value?: string) => (value ? undefined : "Required");

const getAddressPrefix = (legalType: string | null) => {
  if (!legalType) {
    return "Address";
  }
  return legalType === "PRIVATE" ? "Personal Address" : "Business Address";
};

export const inputFieldStyles = {
  borderRadius: "10px !important",
  "&:not(.Mui-focused):not(.Mui-error)": {
    "&:not(:hover) fieldset": {
      borderColor: "var(--grey-4)",
    },
    "&:hover fieldset": {
      borderColor: "var(--grey-3)",
    },
  },
} as const;

const selectSx = {
  button: { right: "12px !important" },
  label: {
    mb: "4px !important",
    span: {
      fontSize: "13px !important",
    },
  },
  " .MuiInputBase-root": {
    ...inputFieldStyles,
  },
} as const;

const CustomHelperText = ({ children }: { children: ReactNode }) => {
  return (
    <Typography component="span" size="small" color="grey-2">
      {children}
    </Typography>
  );
};

const AddTransferWiseWithdrawalFormComponent = ({
  dispatch,
  legalType,
  recipientType,
  currency,
  fetchingTransferWiseCurrencies,
  transferWiseCurrencies,
  formRequirements,
  submit,
  submitting,
  error,
  initialValues,
  change,
  creatingWithdrawalMethod,
}: AddTransferWiseWithdrawalFormProps) => {
  useEffect(() => {
    void dispatch(fetchTransferWiseCurrencies());
  }, [dispatch]);

  const isFirstRender = useRef(true);
  const changeRef = useLatestCallback((field: string, value: unknown) =>
    change?.(field, value),
  );
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    changeRef("recipient_type", initialValues?.recipient_type);
  }, [initialValues?.recipient_type, changeRef]);

  const addressPrefix = getAddressPrefix(legalType);

  const fields = recipientType ? formRequirements[recipientType]?.fields : [];
  const isLoadingFields = !fields?.length && creatingWithdrawalMethod;
  const showLoader =
    fetchingTransferWiseCurrencies ||
    !transferWiseCurrencies.length ||
    isLoadingFields;

  const showFields = !!transferWiseCurrencies.length;

  const hasNoFields =
    !!currency &&
    !creatingWithdrawalMethod &&
    !Object.keys(formRequirements).length;

  return (
    <Form
      onSubmit={submitting ? null : submit}
      error={error}
      autoComplete="off"
    >
      <AddWithdrawalMethodOTPModalInstance />

      {showFields && (
        <Stack sx={{ gap: 2 }}>
          <Field
            id="currency"
            name="currency"
            // autoComplete - please see: https://stackoverflow.com/a/30976223
            autoComplete="new-currency"
            component={SelectField}
            validate={[requiredValidator]}
            options={transferWiseCurrencies.map((key) => ({
              label: key,
              value: key,
            }))}
            label="Receiving Currency"
            FormControlProps={{
              sx: selectSx,
            }}
            helperText={
              <CustomHelperText>
                Where your bank holds your money. Drives the method options
                below.
              </CustomHelperText>
            }
          />
          <Field
            key="account_name"
            id="account_name"
            name="account_name"
            // autoComplete - please see: https://stackoverflow.com/a/30976223
            autoComplete="new-account_name"
            component={TextField}
            label="Full Name of the Account Holder"
            labelSize="small"
            sx={{
              label: { mb: "4px !important" },
              " .MuiInputBase-root": {
                ...inputFieldStyles,
              },
            }}
            helperText={
              <CustomHelperText>
                Must match the name on the bank account exactly.
              </CustomHelperText>
            }
          />
          {hasNoFields && (
            <Typography
              component="p"
              sx={{ fontStyle: "italic", mt: 2 }}
              color="grey-1"
            >
              Unsupported currency
            </Typography>
          )}
          {currency && !!Object.keys(formRequirements).length && (
            <>
              <Stack sx={{ mb: 0.75 }}>
                <Typography
                  component="label"
                  sx={{
                    textTransform: "uppercase",
                    color: "var(--grey-1)",
                    fontSize: "11px !important",
                    letterSpacing: "1.2px !important",
                    mb: 1,
                  }}
                >
                  Transfer method
                </Typography>
                <Box sx={{ alignSelf: "start" }}>
                  <Field
                    key="recipient_type"
                    id="recipient_type"
                    name="recipient_type"
                    disableUnselecting
                    sx={{
                      background: "#F9F6FF",
                      border: "1px solid #5D19741A !important",
                    }}
                    component={ToggleButtonGroupField}
                    options={Object.keys(formRequirements).map((key) => ({
                      label: formRequirements[key].title,
                      value: key,
                    }))}
                  />
                </Box>
              </Stack>
              <BankFields fields={fields ?? []} addressPrefix={addressPrefix} />
            </>
          )}
          <input type="submit" className="invisible" />
        </Stack>
      )}

      {showLoader && (
        <Box sx={{ minHeight: "6rem", position: "relative" }}>
          <Loader centered />
        </Box>
      )}
    </Form>
  );
};

const BankField = ({
  formRequirement,
  addressPrefix,
}: {
  formRequirement: WithdrawalFieldRequirement;
  addressPrefix: string;
}) => {
  const { type, key, name, valuesAllowed } = formRequirement;
  const label = formRequirement.is_address_field ? (
    <>
      <span>{addressPrefix}</span>
      &nbsp;- {name}
    </>
  ) : (
    name
  );

  switch (type) {
    case "date":
      return (
        <Field
          key={key}
          id={key}
          name={key}
          component={DateField}
          label={label}
          disableFuture
        />
      );
    case "text":
      return (
        <Field
          key={key}
          id={key}
          name={key}
          component={TextField}
          label={label}
          labelSize="small"
          // autoComplete - please see: https://stackoverflow.com/a/30976223
          autoComplete={`new-${key}`}
          sx={{
            label: { mb: "4px !important" },
            " .MuiInputBase-root": {
              ...inputFieldStyles,
            },
          }}
        />
      );
    case "radio":
    case "select":
      return (
        <Field
          key={key}
          id={key}
          name={key}
          // autoComplete - please see: https://stackoverflow.com/a/30976223
          autoComplete={`new-${key}`}
          component={SelectField}
          options={(valuesAllowed || []).map((el) => ({
            label: el.name,
            value: el.key,
          }))}
          label={label}
          FormControlProps={{
            sx: selectSx,
          }}
        />
      );
    default:
      return null;
  }
};

type Section = {
  label: string;
  fields: string[][];
};

const SECTIONS: Section[] = [
  {
    label: "Bank Details",
    fields: [
      ["Tax registration number (CPF)"], // BRL
      ["Tax registration number (CNPJ)"], // BRL
      ["Customer Reference Number (CRN)"], // AUD
      ["BPAY biller code"], // AUD
      ["bank_account_country"],
      ["prefix"],
      ["extra_number"], // CRC
      ["ID type"],
      ["ID document type"], // CRC
      ["account_type"],
      ["account_number", "routing_number"],
    ],
  },
  {
    label: "Recipient",
    fields: [
      ["legal_type"],
      ["email"], // KRW
      ["date_of_birth"], // KRW
      ["region"], // CLP
      ["country"],
      ["city", "zipcode"],
      ["address1"],
      ["address2"],
    ],
  },
];
const sectionedKeys = new Set(SECTIONS.flatMap((s) => s.fields.flat()));

const BankFields = ({
  fields,
  addressPrefix,
}: {
  fields: WithdrawalFieldRequirement[];
  addressPrefix: string;
}) => {
  const fieldsByKeyOrName = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.key || f.name, f])),
    [fields],
  );

  const remainingFields = useMemo(
    () => fields.filter((f) => !sectionedKeys.has(f.key || f.name)),
    [fields],
  );

  return (
    <Stack sx={{ gap: 1.5 }}>
      {SECTIONS.map((section) => {
        const rows = section.fields
          .map(
            (rowKeys) =>
              rowKeys
                .map((keyOrName) => fieldsByKeyOrName[keyOrName])
                .filter(Boolean) as WithdrawalFieldRequirement[],
          )
          .filter((row) => row.length > 0);

        if (!rows.length) {
          return null;
        }

        return (
          <Stack key={section.label} sx={{ gap: 1.5, mt: 1.25 }}>
            <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
              <Typography
                component="label"
                sx={{
                  textTransform: "uppercase",
                  color: "var(--grey-1)",
                  fontSize: "11px !important",
                  letterSpacing: "1.2px !important",
                  flexShrink: 0,
                }}
              >
                {section.label}
              </Typography>
              <Divider
                flexItem
                color="light-grey"
                sx={{ flex: 1, alignSelf: "center" }}
              />
            </Stack>
            {rows.map((row) => {
              return (
                <Stack
                  key={row.map((f) => f.key || f.name).join("-")}
                  direction="row"
                  sx={{ gap: 2 }}
                >
                  {row.map((field) => (
                    <Box key={field.key || field.name} sx={{ flex: 1 }}>
                      <BankField
                        formRequirement={field}
                        addressPrefix={addressPrefix}
                      />
                    </Box>
                  ))}
                </Stack>
              );
            })}
          </Stack>
        );
      })}
      {remainingFields.map((field) => (
        <BankField
          key={field.key || field.name}
          formRequirement={field}
          addressPrefix={addressPrefix}
        />
      ))}
    </Stack>
  );
};

const mapStateToProps = (
  state: RootState,
  {
    formRequirements,
  }: {
    formRequirements: WithdrawalFormRequirements;
  },
) => ({
  fetchingTransferWiseCurrencies:
    state.withdrawal.fetchingTransferWiseCurrencies,
  transferWiseCurrencies: state.withdrawal.transferWiseCurrencies,
  creatingWithdrawalMethod: state.withdrawal.creatingWithdrawalMethod,
  legalType: selector(state, "legal_type") || null,
  recipientType: selector(state, "recipient_type") || null,
  currency: selector(state, "currency") || null,
  initialValues: {
    currency: selector(state, "currency") || SETTINGS.DEFAULT_CURRENCY,
    account_name:
      selector(state, "account_name") ||
      (state.auth.user && state.auth.user.public_name),
    recipient_type: _.isEmpty(formRequirements)
      ? null
      : Object.keys(formRequirements)[0],
  },
});

let abortController: AbortController | null;
let lastRequestTimeout: null | NodeJS.Timeout = null;

const AddTransferWiseWithdrawalForm = reduxForm<
  {
    currency: string;
    recipient_type: string;
  },
  // TODO: refactor to FC
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
>({
  form: ADD_TRANSFERWISE_WITHDRAWAL_FORM_ID,
  enableReinitialize: false,
  onChange: (
    values,
    dispatch: AppDispatch,
    { formRequirements },
    previousValues,
  ) => {
    const { currency, recipient_type: recipientType } = values;
    const { currency: previousCurrency } = previousValues;

    const clearRedundantFormValues = (
      requirements: WithdrawalFormRequirements,
    ) => {
      if (recipientType && recipientType in requirements) {
        dispatch(
          clearFields(
            ADD_TRANSFERWISE_WITHDRAWAL_FORM_ID,
            false,
            false,
            ..._.without(
              Object.keys(previousValues),
              ...requirements[recipientType].fields.map((fr) => fr.key),
              "currency",
              "account_name",
              "recipient_type",
              "code",
              "is_backup_code",
            ),
          ),
        );
      }
    };

    if (currency !== previousCurrency) {
      return dispatch(fetchCurrencyRequirements(currency as string))
        .then((newRequirements) => clearRedundantFormValues(newRequirements))
        .catch((response) => {
          dispatch(
            stopSubmit(ADD_TRANSFERWISE_WITHDRAWAL_FORM_ID, response.errors),
          );
        });
    }

    if (recipientType && recipientType in formRequirements) {
      clearRedundantFormValues(formRequirements);

      const refreshRequirementsOnChangeFieldsChanged = Object.keys(values)
        .filter(
          (name) =>
            JSON.stringify((values as Record<string, unknown>)[name]) !==
            JSON.stringify((previousValues as Record<string, unknown>)[name]),
        )
        .map((name) =>
          formRequirements[recipientType].fields.find(
            (formRequirement: WithdrawalFieldRequirement) =>
              formRequirement.key === name,
          ),
        )
        .filter((value) => !!value)
        .filter((value) => value.refreshRequirementsOnChange);

      if (!refreshRequirementsOnChangeFieldsChanged.length) {
        return;
      }

      if (abortController) {
        abortController.abort("New requirements request issued");
        abortController = null;
      }

      if (lastRequestTimeout) {
        clearTimeout(lastRequestTimeout);
        lastRequestTimeout = null;
      }

      lastRequestTimeout = setTimeout(() => {
        abortController = new AbortController();
        dispatch(fetchOtherRequirements(values, abortController))
          .then((newRequirements) => {
            clearRedundantFormValues(newRequirements);

            abortController = null;
            lastRequestTimeout = null;
          })
          .catch((error) => {
            if (error?.isCancel) {
              return;
            }
            dispatch(stopSubmit(ADD_TRANSFERWISE_WITHDRAWAL_FORM_ID, error));
          });
      }, 200);
    }
  },
})(AddTransferWiseWithdrawalFormComponent);

const connector = connect(mapStateToProps, mapTypedDispatchToProps);

export default connector(AddTransferWiseWithdrawalForm);
